import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Minus, Plus, Trash2, ArrowRight, Tag, Shield, Truck, MapPin } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import useCart from '@/store/cartStore';
import { EstimatedDelivery, LiveArrivalBadge } from '@/components/ShippingFeatures';

const Cart = () => {
  const { cart, fetchCart, updateQuantity, removeItem, getTotal } = useCart();
  const [deliveryMethod, setDeliveryMethod] = useState('shipping');

  useEffect(() => {
    const loadCart = async () => {
      await fetchCart();
    };
    loadCart();
  }, []);

  const subtotal = getTotal();
  
  // Calculate bulk discount: 4+ of same plant = 20% off (NOT for sale items)
  const bulkDiscount = cart.reduce((acc, item) => {
    if (item.quantity >= 4 && !item.is_on_sale) {
      return acc + (item.price * item.quantity * 0.20);
    }
    return acc;
  }, 0);
  
  const subtotalAfterBulk = subtotal - bulkDiscount;
  // Shipping is FREE for local pickup OR orders over $60
  const shipping = deliveryMethod === 'local_pickup' ? 0 : (subtotalAfterBulk < 60 && subtotalAfterBulk > 0 ? 9.99 : 0);
  const tax = subtotalAfterBulk * 0.11;
  const total = subtotalAfterBulk + shipping + tax;

  // Calculate total savings from sale items
  const totalSavings = cart.reduce((acc, item) => {
    if (item.is_on_sale && item.original_price) {
      return acc + (item.original_price - item.price) * item.quantity;
    }
    return acc;
  }, 0);
  
  // Items qualifying for bulk discount (4+ qty AND not on sale)
  const bulkItems = cart.filter(item => item.quantity >= 4 && !item.is_on_sale);

  if (cart.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="empty-cart">
        <div className="text-center">
          <h2 className="heading-font text-4xl font-bold mb-4">Your cart is empty</h2>
          <p className="text-muted-foreground mb-8">Start adding some beautiful plants to your collection</p>
          <Link to="/shop">
            <Button className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-full" data-testid="continue-shopping-btn">
              Continue Shopping
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-12 md:py-16" data-testid="cart-page">
      <div className="container mx-auto px-4 md:px-8 lg:px-12 max-w-7xl">
        <h1 className="heading-font text-4xl sm:text-5xl md:text-6xl font-bold mb-8 md:mb-12" data-testid="cart-title">Shopping Cart</h1>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-8">
          <div className="lg:col-span-2 space-y-4" data-testid="cart-items">
            {cart.map((item) => {
              // Bulk discount only applies to 4+ qty AND not on sale
              const qualifiesForBulk = item.quantity >= 4 && !item.is_on_sale;
              const itemBulkDiscount = qualifiesForBulk ? item.price * item.quantity * 0.20 : 0;
              
              return (
              <Card key={item.product_id} className={`p-4 md:p-6 ${item.is_on_sale ? 'border-red-200 bg-red-50/30' : ''} ${qualifiesForBulk ? 'border-green-300 bg-green-50/30' : ''}`} data-testid={`cart-item-${item.product_id}`}>
                <div className="flex gap-4">
                  <div className="w-20 h-20 md:w-24 md:h-24 rounded-lg overflow-hidden bg-secondary/30 flex-shrink-0 relative">
                    <img
                      src={item.image_url}
                      alt={item.name}
                      className="w-full h-full object-cover"
                    />
                    {item.is_on_sale && (
                      <div className="absolute top-1 left-1 bg-red-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded flex items-center gap-0.5">
                        <Tag className="w-2.5 h-2.5" />
                        SALE
                      </div>
                    )}
                    {qualifiesForBulk && (
                      <div className="absolute top-1 right-1 bg-green-600 text-white text-[10px] font-bold px-1.5 py-0.5 rounded">
                        20% OFF
                      </div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-base md:text-lg mb-1 truncate" data-testid="cart-item-name">{item.name}</h3>
                    
                    {/* Price Display with Sale Indicator */}
                    {item.is_on_sale && item.original_price ? (
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-lg md:text-xl font-bold text-red-500" data-testid="cart-item-price">
                          ${item.price.toFixed(2)}
                        </span>
                        <span className="text-sm text-muted-foreground line-through">
                          ${item.original_price.toFixed(2)}
                        </span>
                        <span className="text-xs bg-green-100 text-green-700 px-1.5 py-0.5 rounded font-medium">
                          Save ${((item.original_price - item.price) * item.quantity).toFixed(2)}
                        </span>
                      </div>
                    ) : (
                      <p className="text-primary font-bold text-lg" data-testid="cart-item-price">
                        ${item.price.toFixed(2)}
                      </p>
                    )}
                    
                    <p className="text-sm text-muted-foreground mt-1">
                      Qty: {item.quantity} × ${item.price.toFixed(2)} = <span className={`font-semibold ${qualifiesForBulk ? 'line-through text-gray-400' : ''}`}>${(item.price * item.quantity).toFixed(2)}</span>
                      {qualifiesForBulk && (
                        <span className="text-green-600 font-bold ml-2">${(item.price * item.quantity * 0.80).toFixed(2)}</span>
                      )}
                    </p>
                    {qualifiesForBulk && (
                      <p className="text-xs text-green-600 font-medium mt-1">
                        You save ${itemBulkDiscount.toFixed(2)} with bulk discount!
                      </p>
                    )}
                  </div>
                  <div className="flex flex-col items-end justify-between">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => removeItem(item.product_id)}
                      data-testid="remove-item-btn"
                    >
                      <Trash2 className="w-4 h-4 text-destructive" />
                    </Button>
                    <div className="flex items-center gap-1 md:gap-2 bg-secondary rounded-full p-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 md:h-8 md:w-8 rounded-full"
                        onClick={() => updateQuantity(item.product_id, item.quantity - 1)}
                        data-testid="decrease-quantity-btn"
                      >
                        <Minus className="w-3 h-3" />
                      </Button>
                      <span className="w-6 md:w-8 text-center font-semibold text-sm" data-testid="item-quantity">{item.quantity}</span>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 md:h-8 md:w-8 rounded-full"
                        onClick={() => updateQuantity(item.product_id, item.quantity + 1)}
                        data-testid="increase-quantity-btn"
                      >
                        <Plus className="w-3 h-3" />
                      </Button>
                    </div>
                  </div>
                </div>
              </Card>
            );
            })}
          </div>

          <div className="lg:col-span-1">
            <Card className="p-5 md:p-6 sticky top-24" data-testid="cart-summary">
              <h2 className="heading-font text-xl md:text-2xl font-semibold mb-5 md:mb-6">Order Summary</h2>
              
              {/* Delivery Method Selection */}
              <div className="mb-5 pb-5 border-b" data-testid="delivery-method-selection">
                <p className="font-semibold text-sm mb-3">Delivery Method</p>
                <RadioGroup 
                  value={deliveryMethod} 
                  onValueChange={setDeliveryMethod}
                  className="space-y-2"
                >
                  {/* Shipping Option */}
                  <div className={`flex items-center p-3 rounded-lg border-2 transition-all cursor-pointer ${
                    deliveryMethod === 'shipping' 
                      ? 'border-primary bg-primary/5' 
                      : 'border-gray-200 hover:border-gray-300'
                  }`}>
                    <RadioGroupItem value="shipping" id="cart-shipping" />
                    <label htmlFor="cart-shipping" className="flex-1 ml-3 cursor-pointer">
                      <div className="flex items-center gap-2">
                        <Truck className="w-4 h-4 text-primary" />
                        <span className="font-medium text-sm">Ship to Address</span>
                      </div>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        USPS Priority 2-Day • {subtotalAfterBulk >= 60 ? (
                          <span className="text-green-600 font-medium">FREE</span>
                        ) : (
                          '$9.99'
                        )}
                      </p>
                    </label>
                  </div>

                  {/* Local Pickup Option */}
                  <div className={`flex items-center p-3 rounded-lg border-2 transition-all cursor-pointer ${
                    deliveryMethod === 'local_pickup' 
                      ? 'border-primary bg-primary/5' 
                      : 'border-gray-200 hover:border-gray-300'
                  }`}>
                    <RadioGroupItem value="local_pickup" id="cart-local-pickup" />
                    <label htmlFor="cart-local-pickup" className="flex-1 ml-3 cursor-pointer">
                      <div className="flex items-center gap-2">
                        <MapPin className="w-4 h-4 text-primary" />
                        <span className="font-medium text-sm">Local Pickup</span>
                        <span className="text-green-600 font-medium text-xs">FREE</span>
                      </div>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        Springfield, MO • Schedule at checkout
                      </p>
                    </label>
                  </div>
                </RadioGroup>
              </div>
              
              {/* Sale Savings Banner */}
              {totalSavings > 0 && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-4 flex items-center gap-2" data-testid="savings-banner">
                  <Tag className="w-5 h-5 text-green-600" />
                  <div>
                    <p className="text-sm font-semibold text-green-700">Sale Savings!</p>
                    <p className="text-green-600 font-bold">You&apos;re saving ${totalSavings.toFixed(2)}</p>
                  </div>
                </div>
              )}
              
              <div className="space-y-3 md:space-y-4 mb-5 md:mb-6">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Subtotal</span>
                  <span className="font-semibold" data-testid="subtotal">${subtotal.toFixed(2)}</span>
                </div>
                
                {/* Bulk Discount Display */}
                {bulkDiscount > 0 && (
                  <div className="flex justify-between text-green-600">
                    <span className="flex items-center gap-1">
                      <Tag className="w-4 h-4" />
                      Bulk Discount (4+ plants)
                    </span>
                    <span className="font-semibold" data-testid="bulk-discount">-${bulkDiscount.toFixed(2)}</span>
                  </div>
                )}
                
                <div className="flex justify-between">
                  <span className="text-muted-foreground flex items-center gap-1">
                    {deliveryMethod === 'local_pickup' ? (
                      <><MapPin className="w-4 h-4" /> Local Pickup</>
                    ) : (
                      'Shipping'
                    )}
                  </span>
                  <span className="font-semibold" data-testid="shipping">
                    {shipping === 0 ? <span className="text-green-600">FREE</span> : `$${shipping.toFixed(2)}`}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Tax (11%)</span>
                  <span className="font-semibold" data-testid="tax">${tax.toFixed(2)}</span>
                </div>
                <div className="border-t pt-4">
                  <div className="flex justify-between text-lg">
                    <span className="font-semibold">Total</span>
                    <span className="font-bold text-primary" data-testid="total">${total.toFixed(2)}</span>
                  </div>
                </div>
              </div>
              
              {/* Bulk discount info banner */}
              {bulkItems.length > 0 && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-4" data-testid="bulk-discount-banner">
                  <p className="text-sm font-semibold text-green-700 mb-1">🎉 Bulk Discount Applied!</p>
                  <p className="text-xs text-green-600">
                    20% off: {bulkItems.map(item => item.name).join(', ')}
                  </p>
                </div>
              )}
              
              {/* Bulk discount hint for items close to qualifying (not on sale) */}
              {cart.some(item => item.quantity >= 2 && item.quantity < 4 && !item.is_on_sale) && bulkItems.length === 0 && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4" data-testid="bulk-hint">
                  <p className="text-sm text-amber-700">
                    💡 <strong>Tip:</strong> Buy 4+ of the same plant to get 20% off! (excludes sale items)
                  </p>
                </div>
              )}
              
              {deliveryMethod === 'shipping' && subtotalAfterBulk < 60 && subtotalAfterBulk > 0 && (
                <div className="bg-secondary/50 rounded-lg p-3 md:p-4 mb-5 md:mb-6 text-sm" data-testid="shipping-message">
                  Add <span className="font-semibold">${(60 - subtotalAfterBulk).toFixed(2)}</span> more for free shipping!
                </div>
              )}
              
              {/* Estimated Delivery - only for shipping */}
              {deliveryMethod === 'shipping' && (
                <div className="mb-5 md:mb-6">
                  <EstimatedDelivery />
                </div>
              )}
              
              {/* Live Arrival Guarantee */}
              <div className="mb-5 md:mb-6">
                <div className="flex items-center gap-2 text-sm text-green-700 bg-green-50 border border-green-200 rounded-lg p-3">
                  <Shield className="w-4 h-4 flex-shrink-0" />
                  <span>100% Live Arrival Guarantee</span>
                </div>
              </div>
              
              <Link to={`/checkout?delivery=${deliveryMethod}`}>
                <Button className="w-full bg-primary text-primary-foreground hover:bg-primary/90 h-11 md:h-12 rounded-full font-medium" data-testid="checkout-btn">
                  Proceed to Checkout <ArrowRight className="ml-2 w-4 h-4 md:w-5 md:h-5" />
                </Button>
              </Link>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Cart;
